/**
 * PhaseHeatmap — an HTML grid showing risk and complexity level per phase.
 *
 * Rows = phases. Columns = Risk | Complexity.
 * Cells are filled with the category color and show level text.
 * Empty/not-scored cells are muted gray.
 */

import { cn } from '@/lib/utils';
import type { PhaseId, RiskLevel, RCACategory } from '@/types/rca';
import { PHASE_NAMES } from '@/data/rcaQuestions';

// ============================================================
// Helpers
// ============================================================

interface CellColors {
  bg: string;
  border: string;
  text: string;
}

function categoryColors(cat: RCACategory | null | undefined): CellColors {
  switch (cat) {
    case 4:
      return { bg: 'bg-cat-4/15', border: 'border-cat-4/40', text: 'text-cat-4' };
    case 3:
      return { bg: 'bg-cat-3/15', border: 'border-cat-3/40', text: 'text-cat-3' };
    case 2:
      return { bg: 'bg-cat-2/15', border: 'border-cat-2/40', text: 'text-cat-2' };
    case 1:
      return { bg: 'bg-cat-1/15', border: 'border-cat-1/40', text: 'text-cat-1' };
    default:
      return {
        bg: 'bg-muted/40',
        border: 'border-border',
        text: 'text-muted-foreground',
      };
  }
}

function levelDot(level: RiskLevel): string {
  switch (level) {
    case 'High':
      return 'bg-risk-high';
    case 'Medium':
      return 'bg-risk-medium';
    case 'Low':
      return 'bg-risk-low';
    default:
      return 'bg-muted-foreground/30';
  }
}

// ============================================================
// Types
// ============================================================

interface PhaseRow {
  phaseId: PhaseId;
  riskLevel: RiskLevel;
  complexityLevel: RiskLevel;
  category: RCACategory | null;
}

interface PhaseHeatmapProps {
  phases: PhaseRow[];
}

// ============================================================
// Sub-component: single heatmap cell
// ============================================================

function HeatmapCell({
  level,
  category,
  isNA,
}: {
  level: RiskLevel;
  category: RCACategory | null | undefined;
  isNA?: boolean;
}) {
  const colors = categoryColors(isNA ? null : category);

  return (
    <td
      className={cn(
        'px-3 py-2.5 border text-center transition-colors',
        colors.bg,
        colors.border,
      )}
    >
      {isNA ? (
        <span className="text-xs text-muted-foreground/60 font-medium">N/A</span>
      ) : (
        <div className="flex items-center justify-center gap-1.5">
          <span className={cn('w-2 h-2 rounded-full flex-shrink-0', levelDot(level))} />
          <span className={cn('text-sm font-semibold', colors.text)}>{level}</span>
        </div>
      )}
    </td>
  );
}

// ============================================================
// Main Component
// ============================================================

export function PhaseHeatmap({ phases }: PhaseHeatmapProps) {
  if (phases.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">No phase data available.</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="px-4 py-3 text-left font-semibold text-muted-foreground bg-muted/50 border-b border-r border-border">
              Phase
            </th>
            <th className="px-4 py-3 text-center font-semibold text-muted-foreground bg-muted/50 border-b border-r border-border">
              Risk Level
            </th>
            <th className="px-4 py-3 text-center font-semibold text-muted-foreground bg-muted/50 border-b border-border">
              Complexity Level
            </th>
          </tr>
        </thead>
        <tbody>
          {phases.map((row, idx) => {
            const isEventDriven = row.phaseId === 'event_driven';
            return (
              <tr
                key={row.phaseId}
                className={cn(
                  'transition-colors hover:bg-muted/30',
                  idx < phases.length - 1 && 'border-b border-border',
                )}
              >
                <td className="px-4 py-2.5 font-medium text-foreground border-r border-border whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {row.category && (
                      <span
                        className={cn(
                          'text-xs font-bold px-1.5 py-0.5 rounded',
                          row.category === 4 && 'bg-cat-4/20 text-cat-4',
                          row.category === 3 && 'bg-cat-3/20 text-cat-3',
                          row.category === 2 && 'bg-cat-2/20 text-cat-2',
                          row.category === 1 && 'bg-cat-1/20 text-cat-1',
                        )}
                      >
                        Cat {row.category}
                      </span>
                    )}
                    {PHASE_NAMES[row.phaseId]}
                  </div>
                </td>
                <HeatmapCell
                  level={row.riskLevel}
                  category={row.category}
                  isNA={row.riskLevel === 'Not Scored'}
                />
                <HeatmapCell
                  level={row.complexityLevel}
                  category={row.category}
                  isNA={isEventDriven || row.complexityLevel === 'Not Scored'}
                />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
